"use client";

import {
    arrow,
    autoUpdate,
    flip,
    FloatingArrow,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import clsx from "clsx";
import { Children, cloneElement, useRef, useState } from "react";
import styles from "./Tooltip.module.scss";

type ReferenceProps = Partial<{
    [prop: string]: unknown;
    onClick: React.MouseEventHandler;
}>;

export type TooltipProps = {
    /**
     * Node to render inside the tooltip
     */
    content?: React.ReactNode;
    /**
     * Where to put the bubble in relation to the wrapped element
     * @default "bottom"
     */
    placement?:
        | "top"
        | "bottom"
        | "left"
        | "right"
        | "bottom-end"
        | "bottom-start"
        | "top-end"
        | "top-start"
        | "left-end"
        | "left-start"
        | "right-end"
        | "right-start";
    /**
     * Customize the offset in pixels
     * @default 10
     */
    offsetAmount?: number;
    /** Which event should trigger the tooltip
     * @default "hover"
     */
    trigger?: "click" | "hover" | "controlled";
    /**
     * Determines the open state if in controlled mode
     */
    isOpen?: boolean;
    /**
     * Optional callback to set the open state if in controlled mode
     */
    setIsOpen?: (isOpen: boolean) => void;
    /**
     * Aria-describedby attribute for the reference element
     */
    "aria-describedby"?: string;
    children: React.ReactElement<ReferenceProps>;
};

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    children,
    placement = "bottom",
    offsetAmount = 10,
    trigger = "hover",
    isOpen: controlledIsOpen,
    setIsOpen: setControlledIsOpen,
    "aria-describedby": ariaDescribedBy,
}) => {
    const localOpenState = useState(false);
    const [isOpen, setIsOpen] =
        trigger === "controlled"
            ? [controlledIsOpen, setControlledIsOpen]
            : localOpenState;

    const arrowRef = useRef(null);
    const isClick = trigger === "click";
    const isHover = trigger === "hover";

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        middleware: [
            offset(offsetAmount),
            flip({ fallbackAxisSideDirection: "end" }),
            shift(),
            arrow({
                element: arrowRef,
                padding: 16,
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context, {
            move: false,
            enabled: isHover,
        }),
        useFocus(context, {
            enabled: isHover,
        }),
        useClick(context, { enabled: isClick }),
        useDismiss(context),
        useRole(context, { role: "tooltip" }),
    ]);

    const { floatingId } = context;
    const child = Children.only(children);

    const referenceProps: ReferenceProps = getReferenceProps({
        ref: refs.setReference,
    });

    return (
        <>
            {cloneElement(child, {
                ...referenceProps,
                ["aria-expanded"]: isClick ? isOpen : undefined,
                ["aria-controls"]: isClick ? floatingId : undefined,
                ["aria-describedby"]: [ariaDescribedBy, floatingId].join(" "),
                onClick: (event: React.MouseEvent) => {
                    referenceProps.onClick?.(event);
                    child.props.onClick?.(event);
                },
            })}

            {isOpen ? (
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}
                    className={clsx(styles.overlay)}
                >
                    <FloatingArrow
                        ref={arrowRef}
                        context={context}
                        className={styles.overlayArrow}
                        strokeWidth={0.5}
                    />
                    {content}
                </div>
            ) : (
                <div id={floatingId} role="tooltip" hidden>
                    {content}
                </div>
            )}
        </>
    );
};
